import moment from 'moment'
import { CONTACT_FIELDS } from './contactFieldConfig'
import { getAge } from 'app/date.utils'
import type { CardSlotName } from './listCardConfig'
import type { ContactJson } from 'app/types/entities'

export type CardFieldKind = `text` | `value` | `signal`

export interface CardField {
  id: string
  label: string
  slots: CardSlotName[]
  kind: CardFieldKind
  /** True when getValue returns a date; the renderer formats it (e.g. "Jul 22, 2026"). */
  isDate?: boolean
  /** Plain text/value fields return a display value. Signal/location fields render bespoke (see CardSlot). */
  getValue: (contact: ContactJson) => unknown
}

const STANDARD_SLOTS: CardSlotName[] = [`subtitle`, `hero`, `meta`]

export function relativeCommDays(lastCommDate?: string | Date | null): string {
  if (!lastCommDate) return `-`
  const diff = moment().diff(moment(lastCommDate), `days`)
  if (diff === 0) return `Today`
  if (diff === 1) return `Yesterday`
  if (diff > 1) return `${diff} days ago`
  return `In ${Math.abs(diff)} days`
}

const VIRTUAL_FIELDS: CardField[] = [
  { id: `tasks`, label: `Tasks`, slots: [`meta`], kind: `signal`, getValue: () => undefined },
  { id: `aging`, label: `Aging`, slots: [`meta`], kind: `signal`, getValue: () => undefined },
  { id: `location`, label: `Location`, slots: [`subtitle`, `meta`], kind: `text`, getValue: (c) => c.locationId },
  {
    id: `age`,
    label: `Age`,
    slots: [`meta`],
    kind: `text`,
    getValue: (c) => (c.birthDate ? getAge(c.birthDate as any) : undefined),
  },
]

const REGISTRY_FIELDS: CardField[] = CONTACT_FIELDS
  // avoid duplicating virtual ids and skip super-user-only fields from the card menu
  .filter((f) => !VIRTUAL_FIELDS.some((v) => v.id === f.id) && !f.superUserOnly)
  .map((f) => ({
    id: f.id,
    label: f.label,
    slots: STANDARD_SLOTS,
    kind: `text` as const,
    isDate: f.isDate,
    getValue: (c: ContactJson) => f.getValue(c),
  }))

export const cardFieldCatalog: CardField[] = [...VIRTUAL_FIELDS, ...REGISTRY_FIELDS]

export function getCardField(id: string): CardField | undefined {
  return cardFieldCatalog.find((f) => f.id === id)
}

export function fieldsForSlot(slot: CardSlotName): CardField[] {
  return cardFieldCatalog.filter((f) => f.slots.includes(slot))
}

export function isFieldEligible(fieldId: string, slot: CardSlotName): boolean {
  return fieldsForSlot(slot).some((f) => f.id === fieldId)
}
